// frontend/src/pages/manufacturing/v2/CapacityPlanningV2.jsx
//
// V2 port of the v1 Capacity Planning page (frontend/src/pages/CapacityPlanning.js).
// Layout, filters (table/column config), weekly grouping and the click-through
// order-breakdown dialog are all preserved. The only functional differences are:
//   - the daily data feed comes from GET /api/v2/capacity-planning/ (v2-aware
//     availability, same key names as v1);
//   - the per-line order breakdown dialog gains a "V2 Production" column that
//     renders each line's `v2` block (progress bar, "x/y WOs done", produced /
//     scrapped counts, and a subtle "not exploded" state).
//
// Config load/save and the cell-detail line fetch continue to use the v1
// endpoints (there is no v2 equivalent in the contract) — only the daily feed
// is swapped for the v2-aware one.

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Paper, Typography, Box, Button, Alert,
  Table, TableBody, TableCell, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, IconButton, Tooltip, LinearProgress, Divider, Collapse,
} from '@mui/material';
import {
  EventNote, Settings, Refresh, Add, Delete, NotificationsActive,
  ExpandMore, ExpandLess, VisibilityOff, Visibility,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { manufacturingAPI, v2 } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { toast } from 'react-toastify';
import usePageTitle from '../../../hooks/usePageTitle';
import GenerationModeBadge from './components/GenerationModeBadge';

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const formatDateParam = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const todayStr = () => formatDateParam(new Date());

/** Build table rows: overdue header, month headers, week headers (Week 1-5), weekday date rows only. */
const buildTableRows = (dates) => {
  if (!dates || dates.length === 0) return [];

  const today = todayStr();
  const rows = [];
  let currentMonth = null;
  let weekCounter = 0;
  let lastMondayKey = null;
  let overdueHeaderAdded = false;
  let pastOverdueSection = false;

  for (const dateStr of dates) {
    const date = new Date(dateStr + 'T00:00:00');
    const dayOfWeek = date.getDay();

    // Skip weekends
    if (dayOfWeek === 0 || dayOfWeek === 6) continue;

    const isOverdue = dateStr < today;

    if (isOverdue) {
      if (!overdueHeaderAdded) {
        overdueHeaderAdded = true;
        rows.push({ type: 'overdue-header', label: 'Overdue', key: 'overdue-header' });
      }
      rows.push({
        type: 'date',
        dateStr,
        isToday: false,
        isOverdue: true,
        displayDate: `${DAY_NAMES[dayOfWeek]} ${date.getDate()}/${date.getMonth() + 1}`,
        key: dateStr,
      });
      continue;
    }

    if (overdueHeaderAdded && !pastOverdueSection) {
      pastOverdueSection = true;
      currentMonth = null;
      weekCounter = 0;
      lastMondayKey = null;
    }

    const month = date.getMonth();
    const year = date.getFullYear();
    const monthKey = `${year}-${month}`;

    if (monthKey !== currentMonth) {
      currentMonth = monthKey;
      weekCounter = 0;
      lastMondayKey = null;
      rows.push({
        type: 'month-header',
        label: `${MONTH_NAMES[month]} ${year}`,
        key: `month-${monthKey}`,
      });
    }

    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(date);
    monday.setDate(date.getDate() + mondayOffset);
    const mondayKey = formatDateParam(monday);

    if (mondayKey !== lastMondayKey) {
      lastMondayKey = mondayKey;
      weekCounter++;
      rows.push({
        type: 'week-header',
        label: `Week ${weekCounter}`,
        key: `week-${monthKey}-w${weekCounter}`,
      });
    }

    rows.push({
      type: 'date',
      dateStr,
      isToday: dateStr === today,
      isOverdue: false,
      displayDate: `${DAY_NAMES[dayOfWeek]} ${date.getDate()}/${date.getMonth() + 1}`,
      key: dateStr,
    });
  }

  return rows;
};

const CHIP_COLORS = {
  allocated: '#ffa726',   // orange
  shipped: '#4caf50',     // green
  outstanding: '#ef5350', // red
};

/** Render tree-style cell: ordered on top, then allocated (orange), shipped (green), outstanding (red). */
const CellValues = ({ ordered, allocated, shipped, outstanding, hideZeros, hideAllocations, emphasis, onClick }) => {
  const ord = Math.round(ordered);
  const alloc = Math.round(allocated);
  const ship = Math.round(shipped);
  const out = Math.round(outstanding);
  const allZero = ord === 0 && alloc === 0 && ship === 0 && out === 0;
  if (allZero && hideZeros) return null;
  const chipSx = (bg) => ({
    fontSize: emphasis ? '0.75rem' : '0.7rem', fontWeight: 700,
    color: '#fff',
    backgroundColor: bg,
    textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
    borderRadius: '3px', px: 0.5,
  });
  return (
    <Box
      onClick={!allZero && onClick ? onClick : undefined}
      sx={{
        display: 'inline-flex', flexDirection: 'column', alignItems: 'center', minWidth: emphasis ? 56 : 48,
        ...(!allZero && onClick && {
          cursor: 'pointer',
          borderRadius: '4px',
          '&:hover': { outline: '2px solid rgba(25, 118, 210, 0.5)', outlineOffset: '1px' },
        }),
      }}
      title={!allZero && onClick ? 'Click to see order breakdown' : undefined}
    >
      <Box sx={{ fontWeight: emphasis ? 900 : 700, fontSize: emphasis ? '1.1rem' : '0.85rem', ...(emphasis && { textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }) }}>{ord.toLocaleString()}</Box>
      {!allZero && !hideAllocations && <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', gap: 0.5 }}>
        <Box sx={chipSx(CHIP_COLORS.allocated)}>{alloc.toLocaleString()}</Box>
        <Box sx={chipSx(CHIP_COLORS.shipped)}>{ship.toLocaleString()}</Box>
        <Box sx={chipSx(CHIP_COLORS.outstanding)}>{out.toLocaleString()}</Box>
      </Box>}
    </Box>
  );
};

/** Return background colour for a daily total based on alert thresholds. */
const getTotalAlertColor = (value, thresholds) => {
  if (!thresholds || thresholds.red == null || thresholds.orange == null) return undefined;
  const red = Number(thresholds.red);
  const orange = Number(thresholds.orange);
  if (value >= red) return '#ef5350';   // red
  if (value >= orange) return '#ffa726'; // orange
  return '#66bb6a';                      // green
};

/**
 * Compact per-line V2 production cell used inside the order-breakdown dialog.
 * Renders the line's `v2` block: a progress bar, "x/y WOs done" text, and
 * produced / scrapped counts. When the line was never exploded (has_tree
 * false) it shows a subtle "not exploded" hint instead.
 */
export const V2ProductionCell = ({ v2: v2data, testIdSuffix }) => {
  const hasSuffix = testIdSuffix != null;
  if (!v2data || !v2data.has_tree) {
    return (
      <Box data-testid={hasSuffix ? `v2-not-exploded-${testIdSuffix}` : undefined}>
        <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
          not exploded
        </Typography>
      </Box>
    );
  }
  const pct = Math.max(0, Math.min(100, Math.round(v2data.progress_pct || 0)));
  const done = v2data.wo_completed || 0;
  const total = v2data.wo_total || 0;
  const produced = Math.round(v2data.produced_qty || 0);
  const scrapped = Math.round(v2data.scrapped_qty || 0);
  return (
    <Box sx={{ minWidth: 120 }} data-testid={hasSuffix ? `v2-prod-${testIdSuffix}` : undefined}>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct >= 100 ? 'success' : 'primary'}
        sx={{ height: 6, borderRadius: 3, mb: 0.5 }}
      />
      <Typography variant="caption" display="block" sx={{ lineHeight: 1.3 }}>
        {done}/{total} WOs done · {pct}%
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ lineHeight: 1.3 }}>
        Produced {produced.toLocaleString()}
        {scrapped > 0 && (
          <Box component="span" sx={{ color: 'error.main' }}> · Scrap {scrapped.toLocaleString()}</Box>
        )}
      </Typography>
    </Box>
  );
};

/**
 * Compact per-cell V2 production rollup for the grid. The aggregate `/data/`
 * response exposes v2 fields as {date: value} maps, either per column (col.v2)
 * or top-level (td.daily_v2). `v2maps` is that map-of-maps; `dateStr` selects
 * the day. Renders a thin progress bar + "done/total WOs" + scrap, or nothing
 * when there is no v2 activity for that cell (so empty days stay clean).
 */
export const V2AggregateRollup = ({ v2maps, dateStr, inverted, testId }) => {
  if (!v2maps) return null;
  const at = (key) => Number(v2maps[key]?.[dateStr] || 0);
  const total = at('wo_total');
  const done = at('wo_completed');
  const produced = at('produced_qty');
  const scrapped = at('scrapped_qty');
  const hasTreeLines = at('has_tree_lines');
  // Nothing to show for this cell.
  if (total === 0 && produced === 0 && hasTreeLines === 0) return null;
  const pct = Math.max(0, Math.min(100, Math.round(at('progress_pct'))));
  const fg = inverted ? 'rgba(255,255,255,0.9)' : 'text.secondary';
  return (
    <Box sx={{ mt: 0.5, minWidth: 44 }} data-testid={testId}>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={pct >= 100 ? 'success' : 'primary'}
        sx={{
          height: 4, borderRadius: 2, mb: 0.25,
          ...(inverted && { backgroundColor: 'rgba(255,255,255,0.3)' }),
        }}
      />
      <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, display: 'block', color: fg }}>
        {Math.round(done)}/{Math.round(total)} WO · {pct}%
      </Typography>
      {scrapped > 0 && (
        <Typography variant="caption" sx={{ fontSize: '0.6rem', lineHeight: 1.2, display: 'block', color: inverted ? '#ffcdd2' : 'error.main' }}>
          Scrap {Math.round(scrapped).toLocaleString()}
        </Typography>
      )}
    </Box>
  );
};

// --- Legend ---
const LegendChip = ({ color, label }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box sx={{
      backgroundColor: color, color: '#fff', fontWeight: 700, fontSize: '0.7rem',
      textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000',
      borderRadius: '3px', px: 0.75, py: 0.1,
    }}>
      123
    </Box>
    <Typography variant="body2">{label}</Typography>
  </Box>
);

const CapacityLegend = () => (
  <Paper variant="outlined" sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
      <Typography sx={{ fontWeight: 700, fontSize: '0.9rem' }}>123</Typography>
      <Typography variant="body2">Total ordered (due that day)</Typography>
    </Box>
    <LegendChip color={CHIP_COLORS.allocated} label="Allocated — stock reserved, not yet shipped" />
    <LegendChip color={CHIP_COLORS.shipped} label="Shipped — already dispatched" />
    <LegendChip color={CHIP_COLORS.outstanding} label="Outstanding — not allocated or shipped" />
    <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
      Click any number to see the orders behind it (incl. V2 production)
    </Typography>
  </Paper>
);

// --- Cell Details Dialog ---
function CellDetailsDialog({ info, onClose }) {
  const [loading, setLoading] = useState(false);
  const [lines, setLines] = useState([]);

  useEffect(() => {
    if (!info) return;
    setLoading(true);
    setLines([]);
    // v2 details endpoint: same per-line keys as v1, plus `line_id` and a
    // per-line `v2` block rendered by the V2 Production column below.
    v2.capacityPlanning.getDetails({
      date: info.date,
      columns: JSON.stringify(info.columns),
    })
      .then(res => {
        const sorted = [...(res.data.lines || [])].sort((a, b) => {
          const aHasOut = a.outstanding > 0 ? 1 : 0;
          const bHasOut = b.outstanding > 0 ? 1 : 0;
          if (aHasOut !== bHasOut) return bHasOut - aHasOut;
          if (a.outstanding !== b.outstanding) return b.outstanding - a.outstanding;
          return (a.order_number || '').localeCompare(b.order_number || '');
        });
        setLines(sorted);
      })
      .catch(() => toast.error('Failed to load order details'))
      .finally(() => setLoading(false));
  }, [info]);

  const numCell = (value, color) => (
    <TableCell align="right" sx={{ fontWeight: 600, px: 1, color: Math.round(value) > 0 ? color : 'text.disabled' }}>
      {Math.round(value).toLocaleString()}
    </TableCell>
  );

  const totals = lines.reduce((acc, l) => ({
    ordered: acc.ordered + l.quantity_ordered,
    allocated: acc.allocated + l.allocated,
    shipped: acc.shipped + l.shipped,
    outstanding: acc.outstanding + l.outstanding,
  }), { ordered: 0, allocated: 0, shipped: 0, outstanding: 0 });

  return (
    <Dialog open={!!info} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        {info?.title}
        <Typography variant="body2" color="text.secondary">{info?.displayDate}</Typography>
      </DialogTitle>
      <DialogContent>
        {loading && <LinearProgress sx={{ mb: 2 }} data-testid="details-loading" />}
        {!loading && lines.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>No order lines found for this date.</Typography>
        )}
        {lines.length > 0 && (
          <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ '& th, & td': { px: 1 } }} data-testid="details-table">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Order #</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Customer</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Product</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ordered</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.allocated }}>Allocated</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.shipped }}>Shipped</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: CHIP_COLORS.outstanding }}>Outstanding</TableCell>
                <TableCell sx={{ fontWeight: 'bold', minWidth: 130 }}>
                  V2 Production <GenerationModeBadge sx={{ height: 16, '& .MuiChip-label': { px: 0.5, fontSize: '0.6rem' } }} />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                <TableCell sx={{ width: 44 }} />
              </TableRow>
            </TableHead>
            <TableBody>
              {lines.map((l, idx) => (
                <TableRow key={l.line_id != null ? `line-${l.line_id}` : idx} hover data-testid={`details-row-${idx}`}>
                  <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 600 }}>{l.order_number}</TableCell>
                  <TableCell sx={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={l.customer_name}>{l.customer_name}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{l.product_code}</TableCell>
                  <TableCell align="right">{Math.round(l.quantity_ordered).toLocaleString()}</TableCell>
                  {numCell(l.allocated, CHIP_COLORS.allocated)}
                  {numCell(l.shipped, CHIP_COLORS.shipped)}
                  {numCell(l.outstanding, CHIP_COLORS.outstanding)}
                  <TableCell>
                    <V2ProductionCell v2={l.v2} testIdSuffix={idx} />
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap', textTransform: 'capitalize' }}>
                    {(l.order_status || '').replace(/_/g, ' ')}
                  </TableCell>
                  <TableCell sx={{ px: 0.5 }}>
                    {l.order_id && (
                      <Tooltip title={`View order ${l.order_number}`}>
                        <IconButton
                          size="small"
                          color="primary"
                          component={RouterLink}
                          to={`/manufacturing/customer-orders/${l.order_id}`}
                        >
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ '& td': { borderTop: '2px solid rgba(0,0,0,0.2)', fontWeight: 700 } }}>
                <TableCell colSpan={3}>Total</TableCell>
                <TableCell align="right">{Math.round(totals.ordered).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.allocated }}>{Math.round(totals.allocated).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.shipped }}>{Math.round(totals.shipped).toLocaleString()}</TableCell>
                <TableCell align="right" sx={{ color: CHIP_COLORS.outstanding }}>{Math.round(totals.outstanding).toLocaleString()}</TableCell>
                <TableCell colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// --- Tables Config Dialog ---
function TablesConfigDialog({ open, tables, onTablesChange, onSave, onClose }) {
  const [expanded, setExpanded] = useState({});

  const toggleExpand = (idx) => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }));

  const handleAddTable = () => {
    onTablesChange([...tables, { name: '', columns: [{ name: '', prefixes: [] }], alert_thresholds: {} }]);
    setExpanded(prev => ({ ...prev, [tables.length]: true }));
  };

  const handleRemoveTable = (index) => onTablesChange(tables.filter((_, i) => i !== index));

  const handleTableNameChange = (index, name) => {
    const updated = [...tables];
    updated[index] = { ...updated[index], name };
    onTablesChange(updated);
  };

  const handleAddColumn = (tableIdx) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      columns: [...updated[tableIdx].columns, { name: '', prefixes: [] }],
    };
    onTablesChange(updated);
  };

  const handleRemoveColumn = (tableIdx, colIdx) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      columns: updated[tableIdx].columns.filter((_, i) => i !== colIdx),
    };
    onTablesChange(updated);
  };

  const handleColumnNameChange = (tableIdx, colIdx, name) => {
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], name };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handlePrefixesChange = (tableIdx, colIdx, prefixString) => {
    const prefixes = prefixString.split(',').map(p => p.trim()).filter(p => p.length > 0);
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], prefixes, _raw: prefixString };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handleExclusionsChange = (tableIdx, colIdx, exclusionString) => {
    const exclusions = exclusionString.split(',').map(p => p.trim()).filter(p => p.length > 0);
    const updated = [...tables];
    const cols = [...updated[tableIdx].columns];
    cols[colIdx] = { ...cols[colIdx], exclusions, _rawExcl: exclusionString };
    updated[tableIdx] = { ...updated[tableIdx], columns: cols };
    onTablesChange(updated);
  };

  const handleAlertChange = (tableIdx, field, value) => {
    const updated = [...tables];
    updated[tableIdx] = {
      ...updated[tableIdx],
      alert_thresholds: { ...updated[tableIdx].alert_thresholds, [field]: value },
    };
    onTablesChange(updated);
  };

  const handleSave = () => {
    for (const t of tables) {
      if (!t.name.trim()) { toast.error('Each table must have a name'); return; }
      if (t.columns.length === 0) { toast.error(`Table "${t.name}" must have at least one column`); return; }
      for (const c of t.columns) {
        if (!c.name.trim() || c.prefixes.length === 0) {
          toast.error(`All columns in "${t.name}" must have a name and at least one prefix`);
          return;
        }
      }
      const at = t.alert_thresholds;
      if (at && (at.red || at.orange)) {
        const r = Number(at.red);
        const o = Number(at.orange);
        if (at.red && at.orange && !isNaN(r) && !isNaN(o) && o >= r) {
          toast.error(`In "${t.name}", orange threshold must be less than red`);
          return;
        }
      }
    }
    const clean = tables.map(t => ({
      ...t,
      columns: t.columns.map(({ _raw, _rawExcl, ...rest }) => rest),
      alert_thresholds: cleanThresholds(t.alert_thresholds),
    }));
    onSave(clean);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Configure Capacity Tables</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Each table appears as a column group. Configure columns and alert thresholds per table.
        </Typography>

        {tables.map((table, tIdx) => (
          <Paper key={tIdx} variant="outlined" sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', p: 1.5, gap: 1 }}>
              <IconButton size="small" onClick={() => toggleExpand(tIdx)}>
                {expanded[tIdx] ? <ExpandLess /> : <ExpandMore />}
              </IconButton>
              <TextField
                label="Table Name"
                value={table.name}
                onChange={(e) => handleTableNameChange(tIdx, e.target.value)}
                size="small"
                sx={{ flex: 1 }}
                placeholder="e.g., Envelopes"
              />
              <IconButton color="error" onClick={() => handleRemoveTable(tIdx)}>
                <Delete />
              </IconButton>
            </Box>

            <Collapse in={expanded[tIdx]}>
              <Box sx={{ px: 2, pb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 1, mt: 1 }}>Columns</Typography>
                {table.columns.map((col, cIdx) => (
                  <Box key={cIdx} sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'flex-start' }}>
                    <TextField
                      label="Column Name"
                      value={col.name}
                      onChange={(e) => handleColumnNameChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., Envelopes"
                    />
                    <TextField
                      label="Prefixes (comma-separated)"
                      value={col._raw !== undefined ? col._raw : col.prefixes.join(', ')}
                      onChange={(e) => handlePrefixesChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., EN/, AB/"
                    />
                    <TextField
                      label="Exclusions (comma-separated)"
                      value={col._rawExcl !== undefined ? col._rawExcl : (col.exclusions || []).join(', ')}
                      onChange={(e) => handleExclusionsChange(tIdx, cIdx, e.target.value)}
                      size="small"
                      sx={{ flex: 1 }}
                      placeholder="e.g., EN/SP"
                    />
                    <IconButton color="error" onClick={() => handleRemoveColumn(tIdx, cIdx)} size="small" sx={{ mt: 0.5 }}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button size="small" startIcon={<Add />} onClick={() => handleAddColumn(tIdx)} variant="text">
                  Add Column
                </Button>

                <Divider sx={{ my: 1.5 }} />
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  <NotificationsActive fontSize="small" sx={{ verticalAlign: 'middle', mr: 0.5 }} />
                  Alert Thresholds (Total column)
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField
                    label="Red (at or above)"
                    value={table.alert_thresholds?.red ?? ''}
                    onChange={(e) => handleAlertChange(tIdx, 'red', e.target.value)}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { backgroundColor: '#ef535033' } }}
                  />
                  <TextField
                    label="Orange (at or above)"
                    value={table.alert_thresholds?.orange ?? ''}
                    onChange={(e) => handleAlertChange(tIdx, 'orange', e.target.value)}
                    size="small"
                    type="number"
                    sx={{ flex: 1 }}
                    InputProps={{ sx: { backgroundColor: '#ffa72633' } }}
                  />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  Leave blank to disable alerts for this table.
                </Typography>
              </Box>
            </Collapse>
          </Paper>
        ))}

        <Button startIcon={<Add />} onClick={handleAddTable} variant="outlined" fullWidth>
          Add Table
        </Button>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={tables.length === 0}>
          Save Configuration
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Clean alert thresholds: return {red, orange} with numbers, or empty object. */
const cleanThresholds = (at) => {
  if (!at) return {};
  const r = at.red !== '' && at.red != null ? Number(at.red) : null;
  const o = at.orange !== '' && at.orange != null ? Number(at.orange) : null;
  if (r != null && o != null && !isNaN(r) && !isNaN(o)) return { red: r, orange: o };
  return {};
};

// --- Main Page ---
const CapacityPlanningV2 = () => {
  usePageTitle('Capacity Planning V2');
  const { user } = useAuth();

  const [tables, setTables] = useState([]);
  const [tableData, setTableData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [editingTables, setEditingTables] = useState([]);
  const [detailInfo, setDetailInfo] = useState(null);
  const [hideZeros, setHideZeros] = useState(() => localStorage.getItem('capacity_hideZeros') === 'true');
  const [hideAllocations, setHideAllocations] = useState(() => localStorage.getItem('capacity_hideAllocations') === 'true');

  const toggleHideZeros = () => {
    setHideZeros(prev => {
      const next = !prev;
      localStorage.setItem('capacity_hideZeros', String(next));
      return next;
    });
  };

  const toggleHideAllocations = () => {
    setHideAllocations(prev => {
      const next = !prev;
      localStorage.setItem('capacity_hideAllocations', String(next));
      return next;
    });
  };

  const isAdmin = user?.is_staff || user?.role === 'admin';

  // Load global config (shared with v1 — config is not v2-specific).
  useEffect(() => {
    manufacturingAPI.capacityPlanning.getConfig().then(res => {
      const cfg = res.data;
      if (cfg.tables && cfg.tables.length > 0) {
        setTables(cfg.tables);
      }
    }).catch(() => {});
  }, []);

  const totalColumns = useMemo(() => (
    1 + tables.reduce((sum, t) => sum + (t.columns?.length || 0) + 1, 0)
  ), [tables]);

  const fetchData = useCallback(async () => {
    if (tables.length === 0) {
      setTableData([]);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const start = new Date();
      const end = new Date();
      end.setDate(end.getDate() + 84); // 12 weeks

      const promises = tables.map(t =>
        v2.capacityPlanning.getData({
          start_date: formatDateParam(start),
          end_date: formatDateParam(end),
          columns: JSON.stringify(t.columns.map(c => ({ name: c.name, prefixes: c.prefixes, exclusions: c.exclusions || [] }))),
        }).then(r => r.data)
      );
      const results = await Promise.all(promises);
      setTableData(results);
    } catch (err) {
      console.error('Failed to load capacity data:', err);
      setError('Failed to load capacity planning data');
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [tables]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-refresh every 60 seconds
  useEffect(() => {
    if (tables.length === 0) return;
    const interval = setInterval(fetchData, 60000);
    return () => clearInterval(interval);
  }, [fetchData, tables.length]);

  const tableRows = useMemo(() => {
    const dates = tableData[0]?.dates;
    return buildTableRows(dates);
  }, [tableData]);

  const openDetails = (row, table, column) => {
    const cols = column ? [column] : (table.columns || []);
    setDetailInfo({
      date: row.dateStr,
      displayDate: row.displayDate,
      title: column ? `${table.name} — ${column.name}` : `${table.name} — Total`,
      columns: cols.map(c => ({ name: c.name, prefixes: c.prefixes, exclusions: c.exclusions || [] })),
    });
  };

  const handleSaveTables = (newTables) => {
    setTables(newTables);
    setConfigDialogOpen(false);
    manufacturingAPI.capacityPlanning.updateConfig({ tables: newTables }).catch(() => {});
    toast.success('Configuration saved');
  };

  return (
    <Container maxWidth={false} sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h4" fontWeight={600} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          Capacity Planning
          <GenerationModeBadge />
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {isAdmin && (
            <Button
              variant="outlined"
              startIcon={<Settings />}
              onClick={() => {
                setEditingTables(JSON.parse(JSON.stringify(tables)));
                setConfigDialogOpen(true);
              }}
            >
              Configure Tables
            </Button>
          )}
          <Tooltip title={hideZeros ? 'Show empty cells' : 'Hide empty cells'}>
            <Button
              variant={hideZeros ? 'contained' : 'outlined'}
              startIcon={hideZeros ? <VisibilityOff /> : <Visibility />}
              onClick={toggleHideZeros}
              size="small"
            >
              {hideZeros ? 'Zeros Hidden' : 'Zeros Shown'}
            </Button>
          </Tooltip>
          <Tooltip title={hideAllocations ? 'Show allocations' : 'Hide allocations'}>
            <Button
              variant={hideAllocations ? 'contained' : 'outlined'}
              startIcon={hideAllocations ? <VisibilityOff /> : <Visibility />}
              onClick={toggleHideAllocations}
              size="small"
            >
              {hideAllocations ? 'Allocations Hidden' : 'Allocations Shown'}
            </Button>
          </Tooltip>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
            disabled={loading || tables.length === 0}
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} data-testid="error-state">{error}</Alert>}

      {tables.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center' }} data-testid="empty-state">
          <EventNote sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No tables configured
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
            {isAdmin
              ? 'Configure capacity tables to see planning data.'
              : 'An admin needs to configure the capacity planning tables.'}
          </Typography>
          {isAdmin && (
            <Button
              variant="contained"
              startIcon={<Settings />}
              onClick={() => {
                setEditingTables([]);
                setConfigDialogOpen(true);
              }}
            >
              Configure Tables
            </Button>
          )}
        </Paper>
      )}

      {loading && <LinearProgress sx={{ mb: 2 }} data-testid="loading-state" />}

      {tables.length > 0 && <CapacityLegend />}

      {tableData.length > 0 && tables.length > 0 && (
        <Table size="small" data-testid="capacity-table">
          <colgroup>
            <col style={{ width: 160 }} />
            {tables.map((t, tIdx) => (
              <React.Fragment key={tIdx}>
                {t.columns.map((_, cIdx) => (
                  <col key={`${tIdx}-${cIdx}`} />
                ))}
                <col style={{ width: 100 }} />
              </React.Fragment>
            ))}
          </colgroup>
          <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                sx={{ fontWeight: 'bold', verticalAlign: 'bottom', borderBottom: '2px solid rgba(0,0,0,0.2)' }}
              >
                Date
                <Box sx={{ fontSize: '0.65rem', fontWeight: 400, mt: 0.5, lineHeight: 1.4, display: 'flex', gap: 0.5 }}>
                  <Box component="span" sx={{ color: '#ef6c00' }}>Alloc</Box>
                  <Box component="span" sx={{ color: '#2e7d32' }}>Shipped</Box>
                  <Box component="span" sx={{ color: '#d32f2f' }}>Outstd</Box>
                </Box>
              </TableCell>
              {tables.map((t, tIdx) => (
                <TableCell
                  key={tIdx}
                  align="center"
                  colSpan={(t.columns?.length || 0) + 1}
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '0.95rem',
                    borderLeft: '4px solid rgba(0,0,0,0.6)',
                    borderBottom: '1px solid rgba(0,0,0,0.1)',
                    backgroundColor: 'grey.50',
                  }}
                >
                  {t.name}
                </TableCell>
              ))}
            </TableRow>
            <TableRow>
              {tables.map((t, tIdx) => (
                <React.Fragment key={tIdx}>
                  {(t.columns || []).map((col, cIdx) => (
                    <TableCell
                      key={`${tIdx}-${cIdx}`}
                      align="center"
                      sx={{
                        fontWeight: 'bold',
                        borderLeft: cIdx === 0 ? '4px solid rgba(0,0,0,0.6)' : undefined,
                        pl: cIdx === 0 ? 2 : undefined,
                        borderBottom: '2px solid rgba(0,0,0,0.2)',
                      }}
                    >
                      <Tooltip title={col.prefixes.join(', ')}>
                        <span>{col.name}</span>
                      </Tooltip>
                    </TableCell>
                  ))}
                  <TableCell
                    align="center"
                    sx={{ fontWeight: 'bold', borderBottom: '2px solid rgba(0,0,0,0.2)' }}
                  >
                    Total
                  </TableCell>
                </React.Fragment>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {tableRows.map((row) => {
              if (row.type === 'overdue-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: '#d32f2f', color: '#fff', fontWeight: 700,
                        fontSize: '0.95rem', py: '8px !important', px: '16px !important',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              if (row.type === 'month-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: 'primary.main', color: 'primary.contrastText', fontWeight: 700,
                        fontSize: '0.95rem', py: '8px !important', px: '16px !important',
                        pt: row.key === tableRows.find(r => r.type === 'month-header')?.key ? '0 !important' : '20px !important',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              if (row.type === 'week-header') {
                return (
                  <TableRow key={row.key} sx={{ '& td': { border: 0, p: 0 } }}>
                    <TableCell
                      colSpan={totalColumns}
                      sx={{
                        backgroundColor: 'grey.200', fontWeight: 600, fontSize: '0.82rem',
                        py: '4px !important', px: '16px !important', color: 'text.secondary',
                      }}
                    >
                      {row.label}
                    </TableCell>
                  </TableRow>
                );
              }

              return (
                <TableRow
                  key={row.key}
                  data-testid={`date-row-${row.dateStr}`}
                  sx={{
                    backgroundColor: row.isToday
                      ? 'rgba(25, 118, 210, 0.08)'
                      : row.isOverdue
                        ? 'rgba(211, 47, 47, 0.06)'
                        : 'inherit',
                    '&:hover': { backgroundColor: 'action.selected' },
                  }}
                >
                  <TableCell
                    sx={{
                      fontWeight: row.isToday ? 700 : 500,
                      color: row.isToday ? 'primary.main' : row.isOverdue ? '#d32f2f' : 'inherit',
                      whiteSpace: 'nowrap',
                      pl: 4,
                    }}
                  >
                    {row.isToday ? `Today - ${row.displayDate}` : row.displayDate}
                  </TableCell>
                  {tables.map((t, tIdx) => {
                    const td = tableData[tIdx];
                    if (!td) return null;
                    const total = td.daily_totals[row.dateStr] || 0;
                    const alertBg = row.isOverdue ? '#ef5350' : getTotalAlertColor(total, t.alert_thresholds);
                    return (
                      <React.Fragment key={tIdx}>
                        {(td.columns || []).map((col, cIdx) => (
                          <TableCell
                            key={`${tIdx}-${cIdx}`}
                            align="center"
                            sx={{
                              borderLeft: cIdx === 0 ? '4px solid rgba(0,0,0,0.6)' : undefined,
                              pl: cIdx === 0 ? 2 : undefined,
                              py: 0.5,
                              backgroundColor: row.isOverdue ? '#ef5350' : undefined,
                              color: row.isOverdue ? '#fff' : 'inherit',
                            }}
                          >
                            <CellValues
                              ordered={col.totals[row.dateStr] || 0}
                              allocated={col.allocated?.[row.dateStr] || 0}
                              shipped={col.shipped?.[row.dateStr] || 0}
                              outstanding={col.outstanding?.[row.dateStr] || 0}
                              hideZeros={hideZeros}
                              hideAllocations={hideAllocations}
                              onClick={() => openDetails(row, t, t.columns?.[cIdx])}
                            />
                            <V2AggregateRollup
                              v2maps={col.v2}
                              dateStr={row.dateStr}
                              inverted={row.isOverdue}
                              testId={`v2-rollup-${tIdx}-${cIdx}-${row.dateStr}`}
                            />
                          </TableCell>
                        ))}
                        <TableCell
                          align="center"
                          sx={{ backgroundColor: alertBg, color: alertBg ? '#fff' : 'inherit', py: 0.5 }}
                        >
                          <CellValues
                            ordered={total}
                            allocated={td.daily_allocated?.[row.dateStr] || 0}
                            shipped={td.daily_shipped?.[row.dateStr] || 0}
                            outstanding={td.daily_outstanding?.[row.dateStr] || 0}
                            hideZeros={hideZeros}
                            hideAllocations={hideAllocations}
                            emphasis
                            onClick={() => openDetails(row, t, null)}
                          />
                          <V2AggregateRollup
                            v2maps={td.daily_v2}
                            dateStr={row.dateStr}
                            inverted={!!alertBg || row.isOverdue}
                            testId={`v2-rollup-total-${tIdx}-${row.dateStr}`}
                          />
                        </TableCell>
                      </React.Fragment>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <CellDetailsDialog info={detailInfo} onClose={() => setDetailInfo(null)} />

      <TablesConfigDialog
        open={configDialogOpen}
        tables={editingTables}
        onTablesChange={setEditingTables}
        onSave={handleSaveTables}
        onClose={() => setConfigDialogOpen(false)}
      />
    </Container>
  );
};

export default CapacityPlanningV2;
